import React from 'react';
import { Gift, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { formatMoney } from '../../utils/formatMoney';
import { SOI_DAO_STATUS_LABELS } from '../../utils/soiDaoUtils';

export function SoiDaoPrizePanel({ prizes = [], entryCount = 0 }) {
  const slots = Array.from({ length: 5 }, (_, index) => {
    const prize = prizes[index];
    if (!prize) {
      return {
        slot: index + 1,
        amount: 0,
        status: 'accumulating',
        milestone: undefined,
        winningNumber: undefined,
      };
    }
    return prize;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Gift className="h-5 w-5 text-brand-gold" />
          รางวัล 5 ช่อง
        </CardTitle>
        <p className="text-xs text-gray-500">
          เลขรางวัลเปิดเผยทั้งรอบ · จ่ายเมื่อสถานะพร้อมจ่ายและมีผู้ถูก
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {slots.map((prize) => {
          const claimed = prize.status === 'claimed';
          const ready = prize.status === 'ready' || prize.status === 'unclaimed';
          const statusMeta =
            SOI_DAO_STATUS_LABELS[prize.status] ||
            SOI_DAO_STATUS_LABELS.accumulating;
          const milestone = Number(prize.milestone) || 0;
          const progressCount = Number(prize.entryCount ?? entryCount) || 0;
          const progress =
            milestone > 0
              ? Math.min(100, Math.round((progressCount / milestone) * 100))
              : claimed || ready
                ? 100
                : 0;

          return (
            <div
              key={`prize-${prize.slot}`}
              className={`rounded-xl border-2 px-3 py-2.5 ${
                claimed
                  ? 'border-green-300 bg-green-50'
                  : ready
                    ? 'border-brand-gold/50 bg-brand-gold-light/20'
                    : 'border-gray-100 bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-brand-red">
                  รางวัลที่ {prize.slot}
                </p>
                <p className="font-bold text-brand-gold">
                  {formatMoney(prize.amount)}
                </p>
              </div>

              <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                <p className="font-mono text-lg font-bold text-brand-red">
                  {prize.winningNumber || '--'}
                </p>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusMeta.className}`}
                >
                  {statusMeta.text}
                </span>
              </div>

              {claimed ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-700">
                  <span className="inline-flex items-center gap-1">
                    <Star className="h-3 w-3 fill-brand-gold text-brand-gold" />
                    ตำแหน่ง{' '}
                    <span className="font-mono font-bold">
                      {prize.starPosition}
                    </span>
                  </span>
                  <span className="font-semibold text-green-700">
                    จ่าย {formatMoney(prize.payout ?? prize.amount)}
                  </span>
                </div>
              ) : (
                <div className="mt-2">
                  <div className="mb-1 flex justify-between text-[10px] text-gray-500">
                    <span>
                      สะสม {progressCount}
                      {milestone ? ` / ${milestone}` : ''} ดาว
                    </span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className={`h-full rounded-full ${
                        ready ? 'bg-brand-gold' : 'bg-gray-400'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
